import { Form, FormikProvider, useFormik } from "formik";
import { useCallback, useEffect, useEffectEvent } from "react";
import { z } from "zod";
import type { PatPermission } from "@common/schemas/domains/personalAccessToken/personal-access-token.schema";
import {
    UserWorkspaceRole,
    VIEW_ONLY_WRITE_REFUSAL_MESSAGE,
} from "@common/userWorkspaceRoles";
import { useActiveWorkspaceRole } from "@hooks/permissions/useActiveWorkspaceRole";
import { validateFormikWithZodSchema } from "@helpers/form";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { Radio, RadioGroup } from "@design-system/Radio/Radio";
import { VStack } from "@design-system/VStack/VStack";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import {
    PlainModal,
    StandardModalFooter,
    StandardModalHeader,
} from "@design-system/Modal/Modal";
import { useCreatePersonalAccessTokenMutation } from "@api/personalAccessTokens.api";
import { HOTKEYS_SCOPES } from "@hooks/keyboard/useKeyboardCommand";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import styles from "./McpCliSettings.module.scss";

const createPersonalAccessTokenSchema = z.object({
    name: z.string().trim().min(1, "Token name is required").max(120),
    scope: z.enum(["read", "write"]),
});

type CreatePersonalAccessTokenFormValues = z.infer<
    typeof createPersonalAccessTokenSchema
>;

const fields = createFormikFields<CreatePersonalAccessTokenFormValues>();

interface CreatePersonalAccessTokenModalProps {
    show: boolean;
    onCreated: (plaintext: string) => void;
    onCancel: () => void;
}

export const CreatePersonalAccessTokenModal: React.FC<
    CreatePersonalAccessTokenModalProps
> = ({ show, onCreated, onCancel }) => {
    const createMutation = useCreatePersonalAccessTokenMutation();
    const { error: toastError } = useToaster();

    const isViewOnly = useActiveWorkspaceRole() === UserWorkspaceRole.VIEW_ONLY;
    const defaultScope = isViewOnly ? "read" : "write";

    const formik = useFormik<CreatePersonalAccessTokenFormValues>({
        initialValues: {
            name: "",
            scope: defaultScope,
        },
        validate: validateFormikWithZodSchema(createPersonalAccessTokenSchema),
        onSubmit: async (values, helpers) => {
            const permissions: PatPermission[] =
                values.scope === "read" || isViewOnly
                    ? ["read"]
                    : ["read", "write"];
            try {
                const result = await createMutation.mutateAsync({
                    name: values.name.trim(),
                    permissions,
                });
                onCreated(result.plaintext);
                helpers.resetForm({
                    values: { name: "", scope: defaultScope },
                });
            } catch {
                toastError("Failed to create token");
                helpers.setSubmitting(false);
            }
        },
    });

    const handleCancel = useCallback(() => {
        formik.resetForm({ values: { name: "", scope: defaultScope } });
        onCancel();
    }, [formik, onCancel, defaultScope]);

    const downgradeWriteScope = useEffectEvent(() => {
        if (formik.values.scope === "write") {
            formik.setFieldValue("scope", "read");
        }
    });
    useEffect(() => {
        if (isViewOnly) {
            downgradeWriteScope();
        }
    }, [isViewOnly]);

    return (
        <PlainModal show={show} onHide={handleCancel} size="md">
            <FormikProvider value={formik}>
                <Form data-testid="personal-access-token-create-form">
                    <StandardModalHeader>Create token</StandardModalHeader>
                    <VStack gap={16}>
                        <fields.Text
                            name="name"
                            label="Token name"
                            placeholder="e.g. Claude on MacBook Pro"
                            size="xl"
                            required
                        />
                        <VStack gap={8}>
                            <VStack gap={4}>
                                <span className={styles.permissionsLabel}>
                                    Permissions
                                </span>
                                <RadioGroup<
                                    CreatePersonalAccessTokenFormValues["scope"]
                                >
                                    name="scope"
                                    aria-label="Permissions"
                                    value={formik.values.scope}
                                    onValueChange={(scope) =>
                                        formik.setFieldValue("scope", scope)
                                    }
                                    className={styles.permissionsGroup}
                                >
                                    <Tooltip
                                        content={
                                            isViewOnly
                                                ? VIEW_ONLY_WRITE_REFUSAL_MESSAGE
                                                : undefined
                                        }
                                    >
                                        <Radio
                                            value="write"
                                            label="Read & write"
                                            disabled={isViewOnly}
                                        />
                                    </Tooltip>
                                    <Radio value="read" label="Read-only" />
                                </RadioGroup>
                            </VStack>
                            <StandardModalFooter>
                                <Button
                                    variant="secondary"
                                    size="md"
                                    type="button"
                                    onClick={handleCancel}
                                >
                                    Cancel
                                </Button>
                                <ButtonWithLoader
                                    variant="primary"
                                    size="md"
                                    type="submit"
                                    hotKey="mod+enter"
                                    hotKeyOptions={{
                                        description: "Create token",
                                        preventDefault: true,
                                        enableOnFormTags: true,
                                        scopes: [HOTKEYS_SCOPES.SETTINGS_MODAL],
                                    }}
                                    hotKeyDisplayPosition="inline"
                                    loading={createMutation.isPending}
                                    disabled={!formik.isValid}
                                >
                                    Create
                                </ButtonWithLoader>
                            </StandardModalFooter>
                        </VStack>
                    </VStack>
                </Form>
            </FormikProvider>
        </PlainModal>
    );
};
